import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Layout from '../components/layout'
import { SITE, POLICY_DATE } from '../site'
import './legal.css'

const SECTIONS = [
  ['aceptacion', 'Aceptación'],
  ['servicios', 'Los servicios'],
  ['acceso', 'Acceso anticipado y registro'],
  ['google', 'Cuentas de terceros'],
  ['confirmacion', 'Confirmación de acciones'],
  ['uso', 'Uso aceptable'],
  ['ia', 'Limitaciones de la inteligencia artificial'],
  ['disponibilidad', 'Disponibilidad y cambios'],
  ['propiedad', 'Propiedad intelectual'],
  ['responsabilidad', 'Responsabilidad'],
  ['terminacion', 'Suspensión y terminación'],
  ['privacidad', 'Privacidad'],
  ['ley', 'Ley aplicable'],
  ['contacto', 'Contacto'],
]

const Terms = () => (
  <Layout>
    <Helmet>
      <title>Términos y condiciones | Variant</title>
      <meta
        name="description"
        content="Términos y condiciones de uso del sitio de Variant y de Secretario, el asistente personal de inteligencia artificial en WhatsApp."
      />
      <link rel="canonical" href={`${SITE.url}/terminos`} />
    </Helmet>

    <article className="legal container">
      <header className="legal__head">
        <span className="eyebrow">Legal</span>
        <h1>Términos y condiciones</h1>
        <p className="legal__date">Vigentes desde el {POLICY_DATE}</p>
      </header>

      <nav className="legal__toc" aria-label="Contenido">
        <strong>Contenido</strong>
        <ol>
          {SECTIONS.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`}>{label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="legal__body">
        <h2 id="aceptacion">1. Aceptación</h2>
        <p>
          Estos términos regulan el uso del sitio {SITE.url.replace('https://', '')} y de Secretario, servicios de{' '}
          {SITE.legalName} («Variant», «nosotros»). Al usarlos aceptas estos términos y la{' '}
          <Link to="/politica-de-privacidad">política de privacidad</Link>. Si no estás de acuerdo, no uses los
          servicios.
        </p>

        <h2 id="servicios">2. Los servicios</h2>
        <p>
          <strong>Secretario</strong> es un asistente personal de inteligencia artificial que atiende por WhatsApp. A
          partir de tus mensajes y notas de voz consulta y gestiona tu agenda, tus correos, tus tareas, tus contactos y
          tus documentos en las cuentas que conectes, y te envía los recordatorios y encargos que programes.
        </p>
        <p>
          Los servicios para empresas (agentes a la medida, automatización de procesos, DevOps y Cloud) se rigen por la
          propuesta o el contrato que firmemos con cada cliente; estos términos aplican en lo que ese contrato no
          regule.
        </p>

        <h2 id="acceso">3. Acceso anticipado y registro</h2>
        <ul>
          <li>
            Secretario está en acceso anticipado: lo usan las personas que registramos a petición suya. Podemos aceptar
            o rechazar solicitudes y limitar el número de usuarios.
          </li>
          <li>Debes ser mayor de 18 años y usar un número de WhatsApp que sea tuyo.</li>
          <li>
            Eres responsable de lo que se pida a Secretario desde tu número. Si pierdes el control de tu número o de tu
            cuenta de WhatsApp, avísanos de inmediato para suspender el acceso.
          </li>
          <li>
            Durante el acceso anticipado las funciones pueden cambiar. Si en el futuro el servicio tiene un costo, te
            informaremos las condiciones antes de cobrarte y podrás decidir si continúas.
          </li>
        </ul>

        <h2 id="google">4. Cuentas de terceros</h2>
        <p>
          Para algunas funciones conectas cuentas de terceros, como tu cuenta de Google. Al hacerlo autorizas a
          Secretario a acceder a ellas con los permisos que concedes y solo para cumplir tus pedidos, como se explica en
          la <Link to="/politica-de-privacidad#google">política de privacidad</Link>. Puedes desconectarlas en cualquier
          momento. El uso de esas cuentas y de WhatsApp sigue sujeto a los términos de Google y de Meta.
        </p>

        <h2 id="confirmacion">5. Confirmación de acciones</h2>
        <p>
          Antes de ejecutar una acción que cambie algo (enviar un correo, crear, mover o cancelar un evento, crear o
          completar una tarea, crear o compartir un documento), Secretario te describe lo que va a hacer y espera tu
          confirmación. <strong>Revisa la descripción antes de confirmar</strong>: al confirmar, autorizas la acción tal
          como se describió, y sus efectos (por ejemplo, un correo enviado o una invitación a terceros) pueden no tener
          vuelta atrás.
        </p>

        <h2 id="uso">6. Uso aceptable</h2>
        <p>No puedes usar los servicios para:</p>
        <ul>
          <li>enviar spam, mensajes masivos no solicitados o contenido engañoso;</li>
          <li>acosar, amenazar, suplantar o perjudicar a otras personas;</li>
          <li>actividades ilegales o que vulneren derechos de terceros, incluida su privacidad;</li>
          <li>acceder a cuentas o datos que no te pertenecen o para los que no tienes autorización;</li>
          <li>intentar vulnerar la seguridad del servicio, saturarlo o extraer su funcionamiento interno.</li>
        </ul>

        <h2 id="ia">7. Limitaciones de la inteligencia artificial</h2>
        <p>
          Secretario usa modelos de inteligencia artificial que pueden equivocarse, por ejemplo al interpretar una fecha,
          un nombre o una nota de voz. Sus respuestas son una ayuda y no sustituyen tu criterio ni asesoría profesional
          (legal, médica, financiera u otra). Verifica la información importante antes de actuar con base en ella.
        </p>

        <h2 id="disponibilidad">8. Disponibilidad y cambios</h2>
        <p>
          Hacemos lo posible por mantener los servicios disponibles, pero no garantizamos que funcionen sin
          interrupciones ni errores: dependen también de servicios de terceros como WhatsApp, Google y los proveedores de
          inteligencia artificial. Un recordatorio o un encargo puede llegar tarde o no llegar si alguno de ellos falla;
          no uses Secretario como único medio para asuntos críticos. Podemos modificar, suspender o descontinuar
          funciones, y avisaremos con antelación razonable los cambios importantes.
        </p>

        <h2 id="propiedad">9. Propiedad intelectual</h2>
        <p>
          El software, la marca, los logotipos y los contenidos del sitio y de Secretario pertenecen a Variant o a sus
          licenciantes. Te damos una licencia personal, limitada, no exclusiva e intransferible para usar los servicios
          según estos términos. Lo que tú escribes y los datos de tus cuentas siguen siendo tuyos.
        </p>

        <h2 id="responsabilidad">10. Responsabilidad</h2>
        <p>
          En la medida que lo permita la ley, los servicios se ofrecen «tal como están» y Variant no responde por daños
          indirectos, lucro cesante o pérdida de datos derivados de su uso, ni por las acciones que confirmaste, por el
          contenido que tú enviaste ni por fallas de servicios de terceros. Nada de esto limita los derechos que te
          reconoce la ley colombiana de protección al consumidor.
        </p>

        <h2 id="terminacion">11. Suspensión y terminación</h2>
        <p>
          Puedes dejar de usar los servicios cuando quieras y pedirnos la baja. Podemos suspender o terminar tu acceso
          si incumples estos términos, si el uso pone en riesgo el servicio o a terceros, o si dejamos de prestar el
          servicio, avisándote cuando sea posible. Al terminar, tratamos tus datos como indica la política de
          privacidad.
        </p>

        <h2 id="privacidad">12. Privacidad</h2>
        <p>
          El tratamiento de tus datos personales se rige por la{' '}
          <Link to="/politica-de-privacidad">política de privacidad</Link>, que forma parte de estos términos.
        </p>

        <h2 id="ley">13. Ley aplicable</h2>
        <p>
          Estos términos se rigen por las leyes de la República de Colombia. Cualquier controversia se intentará resolver
          primero de forma directa y, si no es posible, ante los jueces competentes de Colombia. Podemos actualizar estos
          términos; la versión vigente es la publicada en esta página.
        </p>

        <h2 id="contacto">14. Contacto</h2>
        <p>
          {SITE.legalName}
          {SITE.nit && <> · NIT {SITE.nit}</>}
          {SITE.city && <> · {SITE.city}</>}
          <br />
          Correo: <a href={`mailto:${SITE.email}`}>{SITE.email}</a> · WhatsApp: {SITE.whatsappDisplay}
        </p>
      </div>
    </article>
  </Layout>
)

export default Terms
